import React, { useState } from 'react'
import { UserX } from 'lucide-react'
import ProfileForm from './Form'
import { useFetchUserDetails } from './api'
import { ROLE_DISPLAY_NAME } from '../Login/api'
import RoleSwitchModal from './RoleSwitchModal'

const ProfilePage: React.FC = () => {
  const [isRoleSwitchModalOpen, setIsRoleSwitchModalOpen] = useState(false)
  const { data, isLoading, isError } = useFetchUserDetails()

  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm font-medium">
          Loading your profile...
        </p>
      </div>
    )
  if (isError)
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <p className="text-red-500 font-medium">Error loading user details</p>
      </div>
    )

  const user = data?.user

  const getInitials = (fullName: string | undefined): string => {
    if (!fullName) return 'N/A'
    const parts = fullName.trim().split(' ')
    if (parts.length === 1) return parts[0][0].toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  }

  const formatName = (fullName: string | undefined): string => {
    if (!fullName) return 'N/A'
    return fullName
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const initials = getInitials(user?.name)
  const displayName = formatName(user?.name)
  const roleName =
    ROLE_DISPLAY_NAME[user?.role as keyof typeof ROLE_DISPLAY_NAME] ||
    'Unknown Role'

  return (
    <div className="mx-auto my-5 max-w-5xl">
      {/* ===== Hero Banner ===== */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1e293b] via-[#334155] to-[#0f172a] shadow-[0_20px_50px_rgba(15,23,42,0.35)]">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 w-64 h-64 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-40 h-40 rounded-full border border-white/5" />
        <div className="absolute bottom-6 right-1/3 w-24 h-24 rounded-full border border-white/5" />

        {/* Pattern dots */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative px-6 py-8 sm:px-10 sm:py-10">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-orange-400 via-amber-400 to-orange-500 p-[3px] shadow-[0_8px_30px_rgba(249,115,22,0.45)]">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-[#1e293b] to-[#334155] border-2 border-white/10 flex items-center justify-center">
                  <span className="text-3xl sm:text-4xl font-bold text-white tracking-wide">
                    {initials}
                  </span>
                </div>
              </div>
              {/* Online dot */}
              <span className="absolute bottom-2 right-2 w-5 h-5 rounded-full bg-emerald-400 border-[3px] border-[#1e293b] shadow-lg" />
            </div>

            {/* Name + Role */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-white truncate">
                  {displayName}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-gradient-to-r from-orange-400/20 to-amber-400/20 text-amber-300 border border-amber-400/30 backdrop-blur-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  {roleName}
                </span>
              </div>
              <p className="text-slate-300/80 text-sm mt-2 max-w-md">
                Manage your personal information, preferences, and account
                settings from here.
              </p>

              {/* Quick stats strip */}
              {/* <div className="flex flex-wrap gap-3 mt-4">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                  <span className="text-amber-400 text-xs">👤</span>
                  <span className="text-[12px] text-slate-200 font-medium">
                    Member Profile
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                  <span className="text-emerald-400 text-xs">🔒</span>
                  <span className="text-[12px] text-slate-200 font-medium">
                    Secure Account
                  </span>
                </div>
              </div> */}
            </div>

            {/* Switch Role Button */}
            <button
              onClick={() => setIsRoleSwitchModalOpen(true)}
              className="self-start sm:self-center inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-[0_6px_20px_rgba(249,115,22,0.4)] hover:shadow-[0_8px_25px_rgba(249,115,22,0.5)] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
            >
              <UserX size={16} />
              Switch Role
            </button>
          </div>
        </div>

        {/* Bottom accent line */}
        <div className="h-1 w-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500" />
      </div>

      {/* ===== Profile Information Card ===== */}
      <div className="mt-6 bg-white rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
        {/* Section header */}
        <div className="relative px-6 sm:px-8 py-5 bg-gradient-to-r from-orange-50/80 via-amber-50/50 to-transparent border-b border-orange-100/60">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-amber-500" />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200">
              <span className="text-white text-lg">📋</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Profile Information
              </h2>
              <p className="text-sm text-gray-500">
                Your personal details and account information
              </p>
            </div>
          </div>
        </div>

        {/* Form body */}
        <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-b from-gray-50/60 to-white">
          <ProfileForm />
        </div>
      </div>

      {/* Role Switch Modal */}
      <RoleSwitchModal
        isOpen={isRoleSwitchModalOpen}
        onClose={() => setIsRoleSwitchModalOpen(false)}
        userData={user}
      />
    </div>
  )
}

export default ProfilePage
